/**
 * SharePublicallyPrompt — discoverability nudge for tree owners.
 *
 * Rendered above the tree-view canvas. Three states (auto-derived from
 * /api/mysteries/tree/:id/status):
 *
 *   not-shared, no leads     → null (nothing useful to nudge)
 *   not-shared, has leads    → full banner: "You have N open leads — let
 *                              other researchers help. [Share publicly] [Not now]"
 *   shared                   → small status pill: "Shared · N leads live ·
 *                              X bounty live ·  [Manage ▾]"
 *
 * The "Not now" CTA persists per-tree in localStorage so a dismissed
 * banner doesn't keep coming back on every page load. The status pill
 * always shows once enabled so the owner can see/manage their state.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../services/api.js'

const DISMISS_KEY = (treeId) => `mysteries.sharePrompt.dismissed.${treeId}`

export default function SharePublicallyPrompt({ treeId, isOwner }) {
  const [status, setStatus]       = useState(null)
  const [saving, setSaving]       = useState(false)
  const [menuOpen, setMenuOpen]   = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (!treeId || !isOwner) return
    setDismissed(!!localStorage.getItem(DISMISS_KEY(treeId)))
    api.get(`/mysteries/tree/${treeId}/status`).then(setStatus).catch(() => {})
  }, [treeId, isOwner])

  if (!isOwner || !status) return null

  async function toggle(enabled) {
    setSaving(true)
    try {
      const r = await api.patch(`/mysteries/tree/${treeId}`, { enabled })
      setStatus(s => ({ ...s, enabled: r.public_mysteries_enabled }))
      // Clear any prior dismissal so the new state is visible.
      localStorage.removeItem(DISMISS_KEY(treeId))
      setDismissed(false)
      setMenuOpen(false)
    } finally {
      setSaving(false)
    }
  }

  function dismiss() {
    localStorage.setItem(DISMISS_KEY(treeId), Date.now().toString())
    setDismissed(true)
  }

  // ── State 1: shared — small status pill, always visible.
  if (status.enabled) {
    return (
      <div className="share-prompt share-prompt--pill">
        <span className="share-prompt__pill-dot" aria-hidden />
        <span>Mysteries shared</span>
        <span className="share-prompt__pill-sep">·</span>
        <span>
          <strong>{status.open_gap_count}</strong> {status.open_gap_count === 1 ? 'lead' : 'leads'}
        </span>
        {status.active_bounty_count > 0 && (
          <>
            <span className="share-prompt__pill-sep">·</span>
            <span>
              <strong>{status.active_bounty_count}</strong> bounty
            </span>
          </>
        )}
        {status.total_solver_attempts > 0 && (
          <>
            <span className="share-prompt__pill-sep">·</span>
            <span><strong>{status.total_solver_attempts}</strong> attempts</span>
          </>
        )}
        <button
          className="share-prompt__pill-menu"
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Manage public mysteries"
        >▾</button>
        {menuOpen && (
          <div className="share-prompt__menu">
            <Link to="/mysteries" className="share-prompt__menu-item">View your live mysteries →</Link>
            <button
              className="share-prompt__menu-item share-prompt__menu-item--danger"
              onClick={() => toggle(false)}
              disabled={saving}
            >Stop sharing</button>
          </div>
        )}
      </div>
    )
  }

  // ── State 2: not shared, no leads — silent.
  if (status.open_gap_count === 0) return null

  // ── State 3: not shared, has leads, banner dismissed — silent.
  // (User can still re-enable later via Tree settings; we don't keep
  // nagging the same person on every visit.)
  if (dismissed) return null

  // ── State 4: full banner.
  return (
    <div className="share-prompt share-prompt--banner">
      <div className="share-prompt__banner-icon" aria-hidden>🔎</div>
      <div className="share-prompt__banner-body">
        <div className="share-prompt__banner-title">
          You have <strong>{status.open_gap_count}</strong> open research lead{status.open_gap_count === 1 ? '' : 's'} on this tree
        </div>
        <div className="share-prompt__banner-body-text">
          Let other family historians help solve them. Your data stays yours — every
          finding still goes through your inbox for review. You can post a bounty to
          attract effort, or just share for free. Disable any time.
        </div>
      </div>
      <div className="share-prompt__banner-actions">
        <button
          className="btn btn-primary btn-sm"
          onClick={() => toggle(true)}
          disabled={saving}
        >{saving ? 'Sharing…' : 'Share publicly →'}</button>
        <button
          className="btn btn-ghost btn-sm share-prompt__banner-dismiss"
          onClick={dismiss}
          disabled={saving}
        >Not now</button>
      </div>
    </div>
  )
}
